<template>
  <div>
    <app-layout class="mb-5">
      <template #header>
        <AppHead title="Cart" />
        <h2 v-if="!isMTD" class="title d-block d-lg-none">Cart</h2>

        <ShopButton :classes="[!isMTD ? 'd-lg-flex d-none' : '']" :title="isMtd() ? 'Customer Cart' : 'Cart'" :link="shopLink" />
        <flash-messages />
      </template>

      <ShopCartItems 
        :cart_items="cart_items" 
        :users="users"
        :total="total"
        :wholesale="false"
        :cartSent="cartSent"
      >
        <div v-if="!isMTD" class="d-none d-lg-block">
          <jet-button icon="fas fa-shopping-cart" btnStyle="success" class="btn-lg px-5" :goesTo="route('cart.checkout')">Checkout</jet-button>
        </div>
        <aside class="mb-3 mt-3 mt-lg-0" v-else >
          <card icon="fas fa-user" headerStyle="primary">
            <template v-if="users.length > 0">
              <Multiselect
                v-model="formCartUser.user_id"
                :options="users"
                placeholder="Select Customer"
                :searchable="true"
                id="user_select" 
                @change="selectUser"
              />
              <div class="mt-3">
                <jet-button icon="fas fa-shopping-bag" class="mr-3 mb-2 w-100" :class="{disabled:!selectedUser}" @click="checkoutAs">
                  Checkout as {{selectedUser && selectedUser.name.length ? selectedUser.name : 'Customer'}} 
                </jet-button>

                <jet-button icon="fas fa-cart-arrow-down" class="w-100" :class="{disabled:!selectedUser}" @click="sendCartPrompt">
                  Send Cart to {{selectedUser && selectedUser.name.length ? selectedUser.name : 'Customer'}}
                </jet-button>
              </div>
            </template>
            <template v-else>
              <h3>Create Customer Account</h3>
            </template>
            <div class="mt-3">

                <div class="form-group">
                  <jet-label class="mb-0" for="phone_number" value="Phone" />
                  <jet-input id="phone_number"  type="text" class="form-control" v-model="formCartUser.phone_number" />
                  <jet-input-error :message="formCartUser.errors.phone_number" class="mt-2" />
                </div>
                <div class="text-center">
                  <strong><em>or</em></strong>
                </div>
                <div class="form-group">
                  <jet-label class="mb-0" for="email" value="Email" />
                  <jet-input id="email"  type="text" class="form-control" v-model="formCartUser.email" />
                  <jet-input-error :message="formCartUser.errors.email" class="mt-2" />
                </div>
              
                <div class="text-center">
                  <strong><em>(optional)</em></strong>
                </div>

                <div class="form-row">
                  <div class="form-group col-12 col-lg-6">
                    <jet-label class="mb-1" for="first_name" value="First name" />
                    <jet-input id="first_name"  type="text" class="form-control" v-model="formCartUser.first_name" />
                    <jet-input-error :message="formCartUser.errors.first_name" class="mt-2" />
                  </div>
                  <div class="form-group col-12 col-lg-6">
                    <jet-label class="mb-1" for="last_name" value="Last name" />
                    <jet-input id="last_name"  type="text" class="form-control" v-model="formCartUser.last_name" />
                    <jet-input-error :message="formCartUser.errors.last_name" class="mt-2" />
                  </div>
                </div>

              <jet-button icon="fas fa-plus" class="mb-2 w-100" @click="checkoutAsNew">
                Checkout as New Customer
              </jet-button>
              <jet-button icon="fas fa-plus" class="w-100" @click="inviteCart">
                Invite and Send Cart
              </jet-button>
            </div>
          </card>
        </aside>
      </ShopCartItems>

      <div v-if="!isMTD" class="cart-bottom-bar d-block d-lg-none">
        <ShopButton :link="shopLink">
          <jet-button class="btn btn-success ml-2" :goesTo="route('cart.checkout')" v-if="!isMTD">Checkout</jet-button>
        </ShopButton>
      </div>

      <jet-dialog-modal icon="fas fa-cart-arrow-down" id="cartSendPromptModal">
        <template #title v-if="selectedUser">
            Send Cart to {{ selectedUser.label }} - Are you sure?
        </template>
        <template #content>
          <div class="form-check">
            <div class="d-flex align-items-center">
              <input type="checkbox" name="email_check" id="email_check" class="form-check-input mb-2" @change="toggleEmail($event)" :checked="formCartUser.send_email == '1'"/>
              <label for="email_check" class="m-0">Send Email</label>
            </div>
          </div>

          <div class="mt-3 pt-3" v-if="selectedUser">
            <h3><i class="fas fa-share text-primary fa-lg mr-2"></i>You Will Send These Items to {{ selectedUser.label }} </h3>
            <div class="mt-3">
              <template v-for="item in cart_items" :key="item.id">
                <ProductRow 
                  :item="item"
                  productStyle="compact"
                  :sku="item.product.sku">
                    <p :class="{'disabled strikethrough':item.discountApplied}" class="m-0">${{formatCurrency(item.original_price)}} x {{item.qty}} <strong>=</strong> <span class="text-success">${{formatCurrency(item.original_price * item.qty)}}</span></p>
                    <p v-if="item.discountApplied" class="m-0">${{formatCurrency(item.discountedPrice)}} x {{item.qty}} <strong>=</strong> <span class="text-success">${{formatCurrency(item.discountedPrice * item.qty)}}</span><i title="Price after discount is applied" class="fas fa-tags text-success ml-2"></i></p>
                </ProductRow>
              </template>
            </div>
            <p class="m-0" :class="{'disabled strikethrough': $page.props.cart_cartFullPriceSubtotal !== $page.props.cart_total}"><strong>Total: </strong>${{formatCurrency($page.props.cart_cartFullPriceSubtotal)}}</p>
            <p class="m-0" v-if="$page.props.cartFullPriceSubtotal !== $page.props.cart_total">
              <strong>Discounted Total: </strong>${{formatCurrency($page.props.cart_total)}}
            </p>
            <p v-if="$page.props.cartFullPriceSubtotal !== $page.props.cart_total" class="m-0 text-secondary text-small">
              You have <span class="text-success">${{formatCurrency($page.props.cartFullPriceSubtotal - $page.props.cart_total)}}</span> applied as a discount
            </p>
          </div>
        </template>

        <template #footer>
          <jet-button icon="fas fa-share" @click="sendCart">Confirm</jet-button>
          <jet-danger-button class="ml-2" @click="hideCartPrompt">Cancel</jet-danger-button>
        </template>
      </jet-dialog-modal>

    </app-layout>
  </div>
</template>

<script>
  import AppLayout from '@/Layouts/AppLayout'
  import JetDialogModal from '@/Jetstream/DialogModal'
  import JetDangerButton from '@/Jetstream/DangerButton'
  import Multiselect from '@vueform/multiselect'
  import ShopButton from '@/Shared/ShopButton'
  import ShopCartItems from '@/Components/Shop/ShopCartItems'
  import ProductRow from '@/Components/Shop/ProductRow'

  import JetInput from '@/Jetstream/Input'
  import JetInputError from '@/Jetstream/InputError'
  import JetLabel from '@/Jetstream/Label'

  export default {
    components: {
      AppLayout,
      Multiselect,
      JetDialogModal,
      JetDangerButton,
      ShopButton,
      ShopCartItems,
      ProductRow,
      JetInput,
      JetInputError,
      JetLabel
    },
    props: {
      cart_items: Array,
      total: Number,
      users: Array
    },
    data() {
      return {
        user_id: null,
        selectedUser: null,
        discountedTotal: false,
        formCartUser: this.$inertia.form({
          user_id: null,
          send_email: 0,
          first_name: null,
          last_name: null,
          phone_number: null,
          email: null,
          invite: false,
        }),
        cartSent: false
      }
    },
    computed: {
      shopLink() {
        return this.isMTD ? route('mtd.shop') : route('shop')
      },
      isMTD() {
        return this.$page.props.auth.user.role == 'mtd'
      }
    },
    methods: {
      checkoutAs() {
        if (this.formCartUser.user_id) {
          this.formCartUser.post(this.route('cart.checkoutas'));
        }
      },
      sendCartPrompt() {
        if (this.formCartUser.user_id) {
          $('#cartSendPromptModal').modal('show');
        }
      },
      hideCartPrompt() {
        $('#cartSendPromptModal').modal('hide');
      },
      toggleEmail(e) {
        this.formCartUser.send_email = e.target.checked ? '1' : '0';
      },
      sendCart() {
        $('#cartSendPromptModal').modal('hide')

        if (this.formCartUser.user_id) {
          this.formCartUser.post(this.route('cart.sendToUser'), {
            onSuccess: () => this.cartSent = !this.cartSent
          })
        }
      },
      checkoutAsNew() {
        this.formCartUser.invite = false;
        this.formCartUser.post(this.route('users.storetucheckout'), {
          onSuccess: () => this.cartSent = !this.cartSent
        })
      },
      inviteCart() {
        this.formCartUser.invite = true;
        this.formCartUser.post(this.route('users.storetucheckout'), {
          onSuccess: () => this.cartSent = !this.cartSent
        })

      },
      selectUser(id) {
        for (var i=0; i<this.users.length; i++) {
          if (this.users[i].value == id) {
            this.selectedUser = this.users[i];
          }
        }
      },
    },
  }
</script>

<style>
  .gallery-thumbnail img {
      width: 120px;
  }
  .cart-items-list {
    list-style: none;
  }
</style>

<style src="@vueform/multiselect/themes/default.css"></style>